import { useMemo } from "react";
import { Chart } from "primereact/chart";
import {
  CHART_GRID_COLOR,
  CHART_PALETTE,
  CHART_TEXT_COLOR,
  baseLegend,
  baseTooltip,
  formatMoney,
  formatMoneyCompact,
  formatNumber,
  withAlpha,
} from "./chartTheme";

export interface ComboSeries {
  label: string;
  data: number[];
  color?: string;
}

interface ComboBarLineChartProps {
  labels: string[];
  // Amount series drawn as bars on the left axis
  bars: ComboSeries[];
  // Count series drawn as lines on the right axis
  lines?: ComboSeries[];
  height?: number;
}

/**
 * Amounts as bars (left axis, $) combined with counts as lines (right axis).
 */
export default function ComboBarLineChart({
  labels,
  bars,
  lines = [],
  height = 320,
}: ComboBarLineChartProps) {
  const data = useMemo(
    () => ({
      labels,
      datasets: [
        ...lines.map((s, i) => {
          const color =
            s.color ?? CHART_PALETTE[(bars.length + i) % CHART_PALETTE.length];
          return {
            type: "line",
            label: s.label,
            data: s.data,
            yAxisID: "yCount",
            borderColor: color,
            backgroundColor: withAlpha(color, 0.15),
            pointBackgroundColor: color,
            pointRadius: 3,
            borderWidth: 2,
            tension: 0.35,
            order: 0,
          };
        }),
        ...bars.map((s, i) => ({
          type: "bar",
          label: s.label,
          data: s.data,
          yAxisID: "yAmount",
          backgroundColor: s.color ?? CHART_PALETTE[i % CHART_PALETTE.length],
          borderRadius: 4,
          maxBarThickness: 34,
          order: 1,
        })),
      ],
    }),
    [labels, bars, lines],
  );

  const options = useMemo(
    () => ({
      maintainAspectRatio: false,
      responsive: true,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: baseLegend,
        tooltip: {
          ...baseTooltip,
          callbacks: {
            label: (ctx: any) =>
              ` ${ctx.dataset.label}: ${
                ctx.dataset.yAxisID === "yCount"
                  ? formatNumber(ctx.parsed.y)
                  : formatMoney(ctx.parsed.y)
              }`,
          },
        },
      },
      scales: {
        x: {
          ticks: { color: CHART_TEXT_COLOR },
          grid: { display: false },
        },
        yAmount: {
          type: "linear",
          position: "left",
          beginAtZero: true,
          ticks: {
            color: CHART_TEXT_COLOR,
            callback: (v: number | string) => formatMoneyCompact(Number(v)),
          },
          grid: { color: CHART_GRID_COLOR },
        },
        yCount: {
          type: "linear",
          position: "right",
          display: lines.length > 0,
          beginAtZero: true,
          ticks: { color: CHART_TEXT_COLOR, precision: 0 },
          grid: { drawOnChartArea: false },
        },
      },
    }),
    [lines.length],
  );

  return (
    <div style={{ position: "relative", height, width: "100%" }}>
      <Chart
        type="bar"
        data={data}
        options={options}
        style={{ height: "100%", width: "100%" }}
      />
    </div>
  );
}
